import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../components/auth/AuthProvider';

export function useHubSpotPortals() {
  const { user } = useAuth();
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPortal, setSelectedPortal] = useState(null);

  // ✅ Fetch list of connected portals and set the active one
  const fetchPortals = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);

      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/hubspot-oauth/portals`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to fetch portals');
      const data = await res.json();

      console.log('DEBUG: Portals response:', data);

      const normalized = (data.portals || []).map(p => ({
        portal_id: p.portal_id,
        hub_domain: p.hub_domain,
        updated_at: p.updated_at
      }));

      setPortals(normalized);

      // ✅ Set the currently active portal
      if (data.activePortalId) {
        const active = normalized.find(p => p.portal_id === data.activePortalId);
        if (active) setSelectedPortal(active);
      } else {
        setSelectedPortal(null);
      }

    } catch (err) {
      console.error('Fetch portals error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // ✅ Select active portal (updates backend + local state)
  const selectPortal = useCallback(async (portalId, hubDomain) => {
    if (!user) return;
    try {
      const token = await user.getIdToken();

      const res = await fetch(`${process.env.REACT_APP_API_URL}/hubspot-oauth/select-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ portal_id: portalId, hub_domain: hubDomain })
      });

      if (!res.ok) throw new Error('Failed to select portal');

      // ✅ Set the selected portal locally
      setSelectedPortal({ portal_id: portalId, hub_domain: hubDomain });

    } catch (err) {
      console.error('Select portal error:', err);
    }
  }, [user]);

  useEffect(() => {
    fetchPortals();
  }, [fetchPortals]);

  return { portals, selectedPortal, selectPortal, fetchPortals, loading, error };
}

